'use client';

import { useEffect, useRef, useState, VideoHTMLAttributes } from 'react';
import {
  PlayWhite,
  PauseWhite,
  RewindWhite,
  FastForwardWhite,
} from '../../assets/icons';
import styles from './VideoPlayer.module.css';

export const VideoPlayer = ({
  className,
  videoProps,
  playable,
  playCallback,
  pauseCallback,
  smallBtn = false,
  borderLessControls = false,
}: {
  className?: string;
  videoProps: VideoHTMLAttributes<HTMLVideoElement>;
  playable?: boolean;
  playCallback?: VoidFunction;
  pauseCallback?: VoidFunction;
  smallBtn?: boolean;
  borderLessControls?: boolean;
}) => {
  const VIDEO_SEEK_TIME = 10;

  const videoRef = useRef<HTMLVideoElement>(null);
  const seekbarProgressElement = useRef<HTMLSpanElement>(null);
  const [isPlaying, setPlaying] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);

  const handlePlayPause = (e: { preventDefault: () => void }) => {
    if (!videoRef.current) return;
    e.preventDefault();
    if (videoRef.current.paused || !videoRef.current.played) {
      videoRef.current.play();
      if (playCallback) playCallback();
    } else {
      videoRef.current.pause();
      if (pauseCallback) pauseCallback();
    }
  };

  const handleOnDivLeave = () => {
    if (isPlaying) {
      setControlsVisible(false);
    }
  };

  const seekVideo = (
    event: { preventDefault: () => void; stopPropagation: () => void },
    input: 'forward' | 'backward',
  ) => {
    event.preventDefault();
    event.stopPropagation();
    if (!videoRef.current) return;

    if (input == 'forward')
      videoRef.current.currentTime =
        videoRef.current.currentTime + VIDEO_SEEK_TIME;
    else if (input == 'backward')
      videoRef.current.currentTime =
        videoRef.current.currentTime - VIDEO_SEEK_TIME;

    videoRef.current.play();
  };

  useEffect(() => {
    if (!videoRef.current) return;
    if (!playable) videoRef.current.pause();
  }, [playable]);

  useEffect(() => {
    if (videoRef.current && seekbarProgressElement.current) {
      videoRef.current.onplay = () => setPlaying(true);
      videoRef.current.onpause = () => setPlaying(false);
      videoRef.current.ontimeupdate = () => {
        if (videoRef.current && seekbarProgressElement.current)
          seekbarProgressElement.current.style.width = `${
            (videoRef.current.currentTime / videoRef.current.duration) * 100
          }%`;
      };
    }
  }, []);

  useEffect(() => {
    if (isPlaying) {
      setControlsVisible(false);
    } else {
      setControlsVisible(true);
    }
  }, [isPlaying]);

  return (
    <div
      className={`${styles.root} ${className ?? ''} ${
        controlsVisible ? 'controlsActive' : ''
      } ${isPlaying ? 'playing' : ''}`}
      onMouseEnter={() => setControlsVisible(true)}
      onClick={handlePlayPause}
      onMouseLeave={handleOnDivLeave}
      aria-hidden="true"
    >
      {}
      <video {...videoProps} ref={videoRef} controls={false} playsInline />
      <div className={styles.controlButtons}>
        {/* rewind */}
        <button
          onClick={(event) => seekVideo(event, 'backward')}
          className={`${styles.controlButtonElement} ${styles.secondaryButtons} ${
            borderLessControls ? styles.borderless : ''
          }`}
        >
          <RewindWhite />
          {VIDEO_SEEK_TIME}
        </button>
        {/* play | pause */}
        <button
          className={`${
            smallBtn
              ? `${styles.controlButtonElement} ${styles['small-btn']}`
              : styles.controlButtonElement
          } ${borderLessControls ? styles.borderless : ''}`}
        >
          {isPlaying ? <PauseWhite /> : <PlayWhite />}
        </button>
        {/* forward */}
        <button
          onClick={(event) => seekVideo(event, 'forward')}
          className={`${styles.controlButtonElement} ${styles.secondaryButtons} ${
            borderLessControls ? styles.borderless : ''
          }`}
        >
          {VIDEO_SEEK_TIME}
          <FastForwardWhite />
        </button>
      </div>
      <div className={styles.seekbar}>
        <span ref={seekbarProgressElement}></span>
      </div>
    </div>
  );
};
